import { useMemo, useState, useEffect, useCallback, useRef } from "react";

import { type Collection } from "@shopify/hydrogen-react/storefront-api-types";
import useSWR from "swr/immutable";

import getCollectionByHandleQuery from "@graphql/get-collection-by-handle.graphql";
import { storefrontAPIFetcher } from "@react/hooks/fetchers";
import { COLLECTION_FILTER_IDS } from "@react/sections/Collection/CollectionFilters/constants";
import { useCollectionFilters } from "@react/sections/Collection/CollectionFiltersContext";
import { useSectionSettings } from "@react/sections/Root/SectionSettingsContext";
import {
  formatCollectionContentCards,
  formatAdjacentCollections,
} from "@react/utilities/collection";
import { STOREFRONT_API_URL } from "@tablescape/constants";
import { isCollectionFilterId } from "@tablescape/react/utilities/collectionFilters";
import {
  type CollectionSectionSettings,
  type CollectionConnection,
  type OPProductConnection,
} from "@tablescape/state";

export const useCollectionParams = [
  STOREFRONT_API_URL,
  getCollectionByHandleQuery,
];

export function useCollection(
  collectionHandle: Collection["handle"] | undefined
) {
  const {
    meta: {
      localization: { country, language },
    },
    pagination: { pageSize: defaultPageSize },
  } = useSectionSettings<CollectionSectionSettings>();

  const [cursor, setCursor] = useState<string | null>(null);
  const [productResults, setProductResults] = useState<OPProductConnection[]>(
    []
  );
  const [pageSize, setPageSize] = useState<number>(defaultPageSize);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingMoreProducts, setIsLoadingMoreProducts] =
    useState<boolean>(false);

  const {
    appliedFilters,
    appliedSort,
    setAvailableFilters,
    setCollectionPriceMax,
  } = useCollectionFilters();

  const sortValue = useMemo(
    () => appliedSort?.value ?? undefined,
    [appliedSort?.value]
  );

  const filterInputs = useMemo(() => {
    return appliedFilters.map((f) => JSON.parse(String(f.input)));
  }, [appliedFilters]);

  const stateKey = useMemo(
    () => JSON.stringify({ filterInputs, sortValue }),
    [filterInputs, sortValue]
  );

  const { data, isLoading: isFetching } = useSWR<CollectionConnection>(
    collectionHandle && country
      ? [
          ...useCollectionParams,
          {
            handle: collectionHandle,
            country,
            language,
            filters: filterInputs,
            pageSize,
            ...(cursor && { cursor }),
            ...(sortValue && {
              sortKey: sortValue.sortKey,
              reverse: sortValue.reverse,
            }),
          },
        ]
      : null,
    storefrontAPIFetcher
  );
  const collection = data?.data?.collection;
  const products = collection?.products;
  const hasNextPage = products?.pageInfo.hasNextPage;
  const endCursor = products?.pageInfo.endCursor;
  const fetchedPageSize = collection?.pageSize?.value;
  const pageOnScroll = collection?.pageOnScroll?.value;

  useEffect(() => {
    if (products?.filters) {
      // Display only whitelisted filters (internal-only filters are excluded)
      const filtered = products.filters.filter((f) =>
        isCollectionFilterId(f.id)
      );
      setAvailableFilters(filtered);
    }
  }, [products?.filters, setAvailableFilters]);

  useEffect(() => {
    const priceFilter = products?.filters?.find(
      (filter) => filter.id === COLLECTION_FILTER_IDS.PRICE
    );

    if (priceFilter?.values?.[0]?.input) {
      const parsed = JSON.parse(String(priceFilter.values[0].input));
      const max = parsed?.price?.max;
      if (typeof max === "number") {
        setCollectionPriceMax(max);
      }
    }
  }, [products?.filters, setCollectionPriceMax, collectionHandle]);

  const filteredProducts = useMemo(() => {
    return products?.edges.map((edge) => edge.node) ?? [];
  }, [products?.edges]);

  // Track state keys to validate results against stale requests
  const expectedFiltersSortRef = useRef<string>("");
  const fetchingFiltersSortRef = useRef<string | null>(null);

  useEffect(() => {
    if (fetchedPageSize) {
      setPageSize(parseInt(fetchedPageSize));
    }
  }, [fetchedPageSize]);

  useEffect(() => {
    // Update state keys when filters/sort change
    expectedFiltersSortRef.current = stateKey;
    // Reset fetching ref to null so it can be captured when fetch actually starts
    fetchingFiltersSortRef.current = null;
    setCursor(null);
    setProductResults([]);
    setIsLoadingMoreProducts(false);
  }, [stateKey, collectionHandle]);

  // Capture state key when fetching starts (for load more)
  useEffect(() => {
    if (isFetching && fetchingFiltersSortRef.current === null) {
      fetchingFiltersSortRef.current = stateKey;
    }
  }, [isFetching, stateKey]);

  useEffect(() => {
    if (isFetching) {
      setIsLoading(true);
      return;
    }

    setIsLoading(false);

    if (filteredProducts.length === 0) {
      return;
    }

    // Process results if initial load or state keys match (prevents stale results)
    const isInitialLoad = expectedFiltersSortRef.current === "";
    const isStateKeyMatch = expectedFiltersSortRef.current === stateKey;
    const isFetchingStateMatch = fetchingFiltersSortRef.current === stateKey;
    const isInitialLoadWithoutFetch =
      fetchingFiltersSortRef.current === null && cursor === null;

    if (
      isInitialLoad ||
      (isStateKeyMatch && (isFetchingStateMatch || isInitialLoadWithoutFetch))
    ) {
      // Replace on filter change, append on load more
      setProductResults((prev) =>
        cursor === null ? filteredProducts : [...prev, ...filteredProducts]
      );
      setIsLoadingMoreProducts(false);
      fetchingFiltersSortRef.current = null;
    }
  }, [filteredProducts, isFetching, stateKey, cursor]);

  const loadMore = useCallback(() => {
    setIsLoadingMoreProducts(true);
    setCursor(endCursor ?? null);
  }, [endCursor]);

  // Parse quick_add_enabled metafield (value is string "true" | "false" from Storefront API)
  const quickAddEnabled = useMemo(() => {
    const value = collection?.quickAddEnabled?.value ?? "";
    return value === "true" || value === "1";
  }, [collection?.quickAddEnabled?.value]);

  // Process variant match filter similar to Liquid template
  const variantMatchFilter = useMemo(() => {
    const rawFilter = collection?.variantMatchFilter?.value ?? "";
    if (!rawFilter) return "";

    // Extract the value after '=' and clean it up
    const parts = rawFilter.split("=");
    const value = parts[parts.length - 1]?.trim() || "";
    return value.replace(/\+/g, " ");
  }, [collection?.variantMatchFilter?.value]);

  // Extract SEO title with fallback: seo.title → titleOverride → title
  const seoTitle = useMemo(() => {
    const seoTitleValue = collection?.seo?.title;
    const titleOverrideValue = collection?.titleOverride?.value;
    const titleValue = collection?.title;

    return seoTitleValue ?? titleOverrideValue ?? titleValue ?? null;
  }, [
    collection?.seo?.title,
    collection?.titleOverride?.value,
    collection?.title,
  ]);

  return {
    adjacentCollections: collection?.adjacentCollections
      ? formatAdjacentCollections(
          collection?.adjacentCollections,
          collection?.disableAdjacentCollectionTitleOverride
        )
      : null,
    breadcrumbs: collection?.breadcrumbs?.references.nodes ?? null,
    contentCards: collection?.contentCards?.references.nodes
      ? formatCollectionContentCards(collection?.contentCards?.references.nodes)
      : null,
    description:
      collection?.descriptionOverride?.value ?? collection?.description ?? null,
    handle: collection?.handle,
    hasNextPage,
    heroImage: collection?.heroImage?.reference.image,
    isLoading,
    isLoadingMore: isLoadingMoreProducts,
    loadMore,
    pageOnScroll: pageOnScroll ? parseInt(pageOnScroll) : null,
    pageSize,
    productResults,
    quickAddEnabled,
    seoTitle,
    sortOptions: collection?.sortOptions?.references.nodes ?? null,
    title: collection?.titleOverride?.value ?? collection?.title ?? null,
    variantMatchFilter,
  };
}
